// src/components/Contact.jsx
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Check } from "lucide-react";
import { useState } from "react";
import TextReveal from "./animations/TextReveal";

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    details: "+216 24 494 492",
    subDetails: "Sun–Thu  ·  8:00 – 17:00",
    href: "tel:+21624494492",
  },
  {
    icon: Mail,
    title: "Email",
    details: "sb.isims@ieee.org",
    subDetails: "We reply within a few days",
    href: "mailto:sb.isims@ieee.org",
  },
  {
    icon: MapPin,
    title: "Location",
    details: "Sfax, rue Tunis klm 11",
    subDetails: "Sakiet Ezzit, Sfax 3021",
  },
];

const field =
  "w-full px-4 py-3.5 bg-[#f8fbff] border border-[#006699]/15 focus:outline-none focus:border-[#006699] focus:ring-1 focus:ring-[#006699] transition-colors text-base text-[#00334d] placeholder-slate-400 rounded-none";
const label = "block text-sm font-semibold text-[#006699] mb-2";

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setFormData({ name: "", email: "", subject: "", message: "" });
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <section id="contact" className="relative w-full bg-[#f4f7f9] py-20 md:pb-32 overflow-hidden border-black mt-10 border-t">
      {/* same background as the gallery */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg,rgba(15,105,157,0.03) 0px,rgba(15,105,157,0.03) 1px,transparent 1px,transparent 18px),
            repeating-linear-gradient(45deg,rgba(15,105,157,0.02) 0px,rgba(15,105,157,0.02) 1px,transparent 1px,transparent 28px)`,
        }}
      />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        {/* One horizontal band: info | form | map. Stacks only on small screens. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:min-h-[720px] gap-px bg-[#006699]/25 p-px shadow-2xl"
        >
          {/* ── 1. Info panel ── */}
          <div className="relative bg-[#00334d] overflow-hidden lg:col-span-4 p-8 sm:p-12 flex flex-col justify-between">
            <div
              className="absolute inset-0 opacity-[0.12] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#7cc4e8] pointer-events-none" />
            <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#7cc4e8] pointer-events-none" />
            <span
              className="absolute -bottom-6 -right-2 text-[160px] sm:text-[240px] leading-none font-black tracking-tighter text-transparent select-none pointer-events-none"
              style={{ WebkitTextStroke: "1.5px rgba(124,196,232,0.3)" }}
              aria-hidden="true"
            >
              Hi
            </span>

            <div className="relative">
              <p className="text-base font-semibold text-[#7dd3fc] mb-3">IEEE ISIMS Student Branch</p>
              <TextReveal
                as="h1"
                className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-[1]"
                stagger={0.06}
              >
                Get in touch
              </TextReveal>
              <p className="mt-5 text-base text-white/70 leading-relaxed max-w-[36ch]">
                Questions, ideas or partnerships? Write to us or come by the campus.
              </p>
            </div>

            <ul className="relative mt-10 divide-y divide-white/15 border-y border-white/15">
              {contactInfo.map((item) => {
                const Icon = item.icon;
                const body = (
                  <>
                    <span className="w-14 h-14 flex-shrink-0 flex items-center justify-center border border-white/25 text-[#7dd3fc] group-hover:bg-white group-hover:text-[#00334d] transition-colors">
                      <Icon className="w-6 h-6" strokeWidth={1.6} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white/55">{item.title}</span>
                      <span className="block text-lg sm:text-xl font-bold text-white truncate">{item.details}</span>
                      <span className="block text-sm text-white/60 mt-0.5">{item.subDetails}</span>
                    </span>
                  </>
                );
                return (
                  <li key={item.title}>
                    {item.href ? (
                      <a href={item.href} className="group flex items-center gap-5 py-5 hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7dd3fc]">
                        {body}
                      </a>
                    ) : (
                      <div className="group flex items-center gap-5 py-5">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── 2. Form ── */}
          <div className="bg-white lg:col-span-4 p-8 sm:p-12 flex flex-col justify-center">
            <h2 className="text-2xl sm:text-3xl font-black text-[#00334d] mb-1 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#006699]" strokeWidth={2} />
              Send a message
            </h2>
            <p className="text-sm text-slate-500 mb-8">All fields are required.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-name" className={label}>Name</label>
                  <input id="c-name" type="text" name="name" value={formData.name} onChange={handleChange} required className={field} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="c-email" className={label}>Email</label>
                  <input id="c-email" type="email" name="email" value={formData.email} onChange={handleChange} required className={field} placeholder="your@email.com" />
                </div>
              </div>
              <div>
                <label htmlFor="c-subject" className={label}>Subject</label>
                <input id="c-subject" type="text" name="subject" value={formData.subject} onChange={handleChange} required className={field} placeholder="Message subject" />
              </div>
              <div>
                <label htmlFor="c-message" className={label}>Message</label>
                <textarea id="c-message" name="message" value={formData.message} onChange={handleChange} required rows="6" className={`${field} resize-none`} placeholder="Your message here…" />
              </div>

              <button
                type="submit"
                className="group w-full h-14 bg-[#006699] hover:bg-[#00334d] text-white font-bold text-base transition-colors flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] focus-visible:ring-offset-2"
              >
                <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
                Send message
              </button>

              <p
                role="status"
                className={`flex items-center gap-2 text-sm font-semibold text-[#006699] transition-opacity duration-300 ${sent ? "opacity-100" : "opacity-0"}`}
              >
                <Check className="w-4 h-4" /> Message sent. Thank you!
              </p>
            </form>
          </div>

          {/* ── 3. Map ── */}
          <div className="bg-white lg:col-span-4 md:col-span-2 flex flex-col">
            <div className="relative flex-1 min-h-[360px] bg-[#00334d]">
              <iframe
                title="ISIMS Location"
                src="https://maps.google.com/maps?q=34.83882,10.75725&z=16&output=embed"
                className="absolute inset-0 w-full h-full grayscale hover:grayscale-0 transition-[filter] duration-700"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#006699] pointer-events-none" />
              <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#006699] pointer-events-none" />
            </div>
            <div className="flex-shrink-0 flex items-center gap-3 px-6 sm:px-8 py-5 bg-white border-t border-[#006699]/10">
              <MapPin className="w-4 h-4 text-[#006699] flex-shrink-0" strokeWidth={2} />
              <p className="text-sm sm:text-base font-semibold text-[#00334d] leading-snug">
                ISIMS, Pôle Technologique de Sfax, Sakiet Ezzit 3021
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}